import { useEffect, useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, Shield, Loader2 } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RoleFormDrawer } from '@/components/roles/RoleFormDrawer';
import { ConfirmModal } from '@/components/ConfirmModal';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';

interface Role {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  isDefault: boolean;
  usersCount: number;
  permissions: Record<string, Record<string, boolean>>;
  createdAt: string;
}

const MODULES = ['Leads', 'Users', 'Tasks', 'Settings'];

function PermissionSummary({ permissions }: { permissions: any }) {
  if (!permissions || typeof permissions !== 'object') return <span className="text-muted-foreground text-xs">—</span>;
  const activeModules = MODULES.filter(m => permissions[m] && Object.values(permissions[m]).some(Boolean));
  if (activeModules.length === 0) return <span className="text-xs text-muted-foreground">No permissions</span>;
  if (activeModules.length === MODULES.length) return <span className="text-xs text-green-600 font-medium">Full access</span>;
  return (
    <span className="text-xs text-slate-600">
      {activeModules.slice(0, 3).join(', ')}{activeModules.length > 3 ? ` +${activeModules.length - 3} more` : ''}
    </span>
  );
}

export function RolesPage() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editRole, setEditRole] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmRole, setConfirmRole] = useState<Role | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get('/roles');
      setRoles(data);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load roles');
    } finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchRoles(); }, []);

  const handleToggleActive = async (role: Role) => {
    // Optimistic flip — revert on failure so the UI does not lie.
    setRoles(prev => prev.map(r => r.id === role.id ? { ...r, isActive: !r.isActive } : r));
    try {
      await api.patch(`/roles/${role.id}/toggle-active`);
    } catch (err: any) {
      setRoles(prev => prev.map(r => r.id === role.id ? { ...r, isActive: role.isActive } : r));
      toast('error', err?.response?.data?.message || 'Failed to update role');
    }
  };

  const handleDelete = (role: Role) => setConfirmRole(role);

  const handleConfirmDelete = async () => {
    if (!confirmRole) return;
    setConfirmLoading(true);
    setDeletingId(confirmRole.id);
    try {
      await api.delete(`/roles/${confirmRole.id}`);
      fetchRoles();
      setConfirmRole(null);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete role');
    } finally {
      setConfirmLoading(false);
      setDeletingId(null);
    }
  };

  const openEdit = (role: Role) => {
    setEditRole(role);
    setShowDrawer(true);
  };

  const { sorted, sort, onSort } = useSort(roles);

  return (
    <DashboardLayout title="Roles">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Roles & Permissions</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{roles.length} roles configured</p>
          </div>
          <Button onClick={() => { setEditRole(null); setShowDrawer(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            New Role
          </Button>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : roles.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Shield className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                <p className="text-base font-medium">No roles yet</p>
                <p className="text-sm mt-1">Create your first role</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <TableHeadRow className="border-b-2 border-slate-200">
                      <SortTh label="Role" sortKey="name" sort={sort} onSort={onSort} />
                      <SortTh label="Description" sortKey="description" sort={sort} onSort={onSort} />
                      <SortTh label="Permissions" sortable={false} />
                      <SortTh label="Users" sortKey="usersCount" sort={sort} onSort={onSort} />
                      <SortTh label="Status" sortKey="isActive" sort={sort} onSort={onSort} />
                      <SortTh label="Actions" sortable={false} />
                    </TableHeadRow>
                  </thead>
                  <tbody>
                    {sorted.map(role => (
                      <tr key={role.id} className="border-b hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="h-8 w-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                              <Shield className="h-4 w-4 text-violet-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{role.name}</p>
                              {role.isDefault && (
                                <span className="text-xs text-slate-400">Default</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-sm">{role.description || '—'}</td>
                        <td className="px-4 py-3">
                          <PermissionSummary permissions={role.permissions} />
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center justify-center rounded-full w-7 h-7 text-xs font-semibold ${role.usersCount > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                            {role.usersCount}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleToggleActive(role)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${role.isActive ? 'bg-blue-600' : 'bg-slate-300'}`}
                            title={role.isActive ? 'Active' : 'Inactive'}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${role.isActive ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEdit(role)}
                              className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(role)}
                              disabled={role.isDefault || deletingId === role.id}
                              className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                              title={role.isDefault ? 'Default roles cannot be deleted' : 'Delete'}
                            >
                              {deletingId === role.id
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Trash2 className="h-3.5 w-3.5" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {showDrawer && (
        <RoleFormDrawer
          role={editRole}
          onClose={() => { setShowDrawer(false); setEditRole(null); }}
          onSaved={() => { setShowDrawer(false); setEditRole(null); fetchRoles(); }}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmRole}
        title="Delete Role?"
        message={`Are you sure you want to delete the role "${confirmRole?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRole(null)}
        isLoading={confirmLoading}
      />
    </DashboardLayout>
  );
}
